import { useEffect } from 'react';
import { motion, MotionConfig } from 'framer-motion';
import '@/styles/brand-kit.css';

/* External Canva Business link — replace this placeholder when the
   official Canva Brand Kit URL is provisioned. Every "Open Canva
   Brand Kit" CTA on the page reads from this single constant. */
const CANVA_BRAND_KIT_URL = 'PASTE_CANVA_BUSINESS_LINK_HERE';

const EASE = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ delay, duration: 0.95, ease: EASE }}
  >
    {children}
  </motion.div>
);

const Swatch = ({ name, hex, usage, dark = false }) => (
  <div className={`bk-swatch${dark ? ' bk-swatch--dark' : ''}`} data-testid={`bk-swatch-${hex.replace('#', '')}`}>
    <div className="bk-swatch__chip" style={{ background: hex }} aria-hidden="true" />
    <div className="bk-swatch__meta">
      <div className="bk-swatch__name">{name}</div>
      <div className="bk-swatch__hex">{hex.toUpperCase()}</div>
      <div className="bk-swatch__use">{usage}</div>
    </div>
  </div>
);

const GradientCard = ({ name, css, label }) => (
  <div className="bk-gradient" data-testid={`bk-gradient-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <div className="bk-gradient__swatch" style={{ background: css }} aria-hidden="true" />
    <div className="bk-gradient__name">{name}</div>
    <div className="bk-gradient__label">{label}</div>
  </div>
);

const ToneCard = ({ title, items, accent }) => (
  <div className={`bk-tone bk-tone--${accent}`}>
    <div className="bk-tone__title">{title}</div>
    <ul className="bk-tone__list">
      {items.map((t) => (
        <li key={t}>{t}</li>
      ))}
    </ul>
  </div>
);

const AssetCard = ({ title, desc, href, label = 'Download', testid }) => (
  <div className="bk-asset" data-testid={testid}>
    <div className="bk-asset__mark" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 6h16M4 12h16M4 18h10" />
      </svg>
    </div>
    <div className="bk-asset__title">{title}</div>
    <div className="bk-asset__desc">{desc}</div>
    <a
      className="bk-btn bk-btn--ghost"
      href={href}
      target={href.startsWith('http') ? '_blank' : undefined}
      rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
      data-testid={`${testid}-cta`}
    >
      {label}
    </a>
  </div>
);

const BrandKitPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <MotionConfig reducedMotion="user">
      <main className="bk-page" data-testid="brand-kit-page">
        {/* ─── Hero ──────────────────────────────────────────────── */}
        <section className="bk-hero" data-testid="bk-hero">
          <div className="bk-hero__glow" aria-hidden="true" />
          <div className="bk-container">
            <Reveal>
              <div className="bk-eyebrow">NYMPHÉ · BRAND KIT</div>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="bk-h1" data-testid="bk-page-title">NYMPHÉ Brand Kit</h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="bk-hero__lede">
                An elegant identity system for a world built on chemistry, privacy, and desire.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <p className="bk-hero__sub">
                Use these assets, colours, typography, and tone guidelines to keep NYMPHÉ
                consistent across the website, social media, Canva Business, press, and
                partner materials.
              </p>
            </Reveal>
            <Reveal delay={0.32}>
              <div className="bk-hero__cta-row">
                <a
                  className="bk-btn bk-btn--primary"
                  href={CANVA_BRAND_KIT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="bk-hero-canva-cta"
                >
                  Open Canva Brand Kit
                </a>
                <a className="bk-btn bk-btn--ghost" href="#assets" data-testid="bk-hero-assets-cta">
                  Download Assets
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.40}>
              <div className="bk-hero__tagline">
                <span className="bk-hero__tagline-mark">·</span>
                <span className="bk-hero__tagline-text">Desire, curated.</span>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ─── 1. Logo System ─────────────────────────────────────── */}
        <section className="bk-section" data-testid="bk-section-logo">
          <div className="bk-container">
            <Reveal>
              <div className="bk-section__eyebrow">01 · Identity</div>
              <h2 className="bk-h2">Logo system</h2>
              <p className="bk-section__lede">
                The wordmark, monogram, and short mark — three forms of one identity.
              </p>
            </Reveal>

            <div className="bk-logo-grid">
              <Reveal delay={0.08}>
                <div className="bk-logo-card bk-logo-card--wide" data-testid="bk-logo-wordmark">
                  <div className="bk-logo-card__mark bk-logo-wordmark">NYMPHÉ</div>
                  <div className="bk-logo-card__label">Primary wordmark</div>
                  <div className="bk-logo-card__use">Website headers, hero sections, media kits, formal brand moments.</div>
                </div>
              </Reveal>
              <Reveal delay={0.16}>
                <div className="bk-logo-card" data-testid="bk-logo-monogram">
                  <div className="bk-logo-card__mark bk-logo-monogram">
                    <span className="bk-logo-monogram__n">N</span>
                    <span className="bk-logo-monogram__crescent" aria-hidden="true" />
                  </div>
                  <div className="bk-logo-card__label">Monogram</div>
                  <div className="bk-logo-card__use">Elegant serif N with a subtle crescent accent — formal applications.</div>
                </div>
              </Reveal>
              <Reveal delay={0.24}>
                <div className="bk-logo-card" data-testid="bk-logo-short">
                  <div className="bk-logo-card__mark bk-logo-short">N</div>
                  <div className="bk-logo-card__label">Short mark</div>
                  <div className="bk-logo-card__use">Favicons, social avatars, app icons, watermarks, small placements.</div>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.32}>
              <div className="bk-rules">
                <div className="bk-rules__col">
                  <div className="bk-rules__title">Do</div>
                  <ul>
                    <li>Use the full wordmark on website headers and formal brand moments.</li>
                    <li>Use the N monogram for favicons, avatars, app icons, watermarks.</li>
                    <li>Keep generous negative space around the logo.</li>
                  </ul>
                </div>
                <div className="bk-rules__col bk-rules__col--avoid">
                  <div className="bk-rules__title">Avoid</div>
                  <ul>
                    <li>Stretching, distorting, or rotating the mark.</li>
                    <li>Recolouring outside the approved palette.</li>
                    <li>Placing the logo over busy or low-contrast imagery.</li>
                  </ul>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ─── 2. Colour Palette ─────────────────────────────────── */}
        <section className="bk-section" data-testid="bk-section-colour">
          <div className="bk-container">
            <Reveal>
              <div className="bk-section__eyebrow">02 · Colour</div>
              <h2 className="bk-h2">Palette</h2>
              <p className="bk-section__lede">
                A cinematic dark base lifted by warm metal and porcelain accents.
              </p>
            </Reveal>

            <Reveal delay={0.08}>
              <div className="bk-swatch-grid">
                <Swatch name="Midnight Noir" hex="#08060A" usage="Deep backgrounds, hero sections, navigation, luxury contrast." dark />
                <Swatch name="Velvet Plum" hex="#2A1024" usage="Premium panels, gradients, overlays, intimate brand depth." dark />
                <Swatch name="Bordeaux Silk" hex="#5A1739" usage="Sensual accents, buttons, highlights, section dividers." dark />
                <Swatch name="Rose Gold" hex="#C9A06A" usage="Luxury details, icons, borders, premium CTA accents, logo highlights." />
                <Swatch name="Champagne Blush" hex="#F4E4D8" usage="Soft backgrounds, cards, editorial sections, warm contrast." />
                <Swatch name="Porcelain" hex="#FFF8F2" usage="Light text areas, refined negative space." />
                <Swatch name="Smoke Mauve" hex="#9C8E96" usage="Secondary text, captions, borders, subtle UI details." />
              </div>
            </Reveal>

            <Reveal delay={0.16}>
              <div className="bk-subhead">Gradients</div>
              <div className="bk-gradient-grid">
                <GradientCard
                  name="Noir → Plum"
                  css="linear-gradient(135deg, #08060A 0%, #2A1024 100%)"
                  label="#08060A → #2A1024"
                />
                <GradientCard
                  name="Plum → Bordeaux"
                  css="linear-gradient(135deg, #2A1024 0%, #5A1739 100%)"
                  label="#2A1024 → #5A1739"
                />
                <GradientCard
                  name="Champagne → Porcelain"
                  css="linear-gradient(135deg, #F4E4D8 0%, #FFF8F2 100%)"
                  label="#F4E4D8 → #FFF8F2"
                />
                <GradientCard
                  name="Rose Gold Glow"
                  css="radial-gradient(ellipse at 30% 30%, rgba(201,160,106,0.35) 0%, rgba(8,6,10,0) 70%), #08060A"
                  label="rgba(201,160,106,0.35)"
                />
              </div>
            </Reveal>
          </div>
        </section>

        {/* ─── 3. Typography ─────────────────────────────────────── */}
        <section className="bk-section" data-testid="bk-section-type">
          <div className="bk-container">
            <Reveal>
              <div className="bk-section__eyebrow">03 · Typography</div>
              <h2 className="bk-h2">Typography</h2>
              <p className="bk-section__lede">
                Elegant, editorial, cinematic. A refined serif display paired with a clean modern sans.
              </p>
            </Reveal>

            <div className="bk-type-grid">
              <Reveal delay={0.08}>
                <div className="bk-type-card" data-testid="bk-type-display">
                  <div className="bk-type-card__label">Display · Hero</div>
                  <div className="bk-type-specimen bk-type-specimen--serif">Desire, curated.</div>
                  <div className="bk-type-card__meta">Playfair Display · Cormorant Garamond · Bodoni-style serif</div>
                  <div className="bk-type-card__hint">Hero headings: cinematic, spacious, refined.</div>
                </div>
              </Reveal>
              <Reveal delay={0.16}>
                <div className="bk-type-card" data-testid="bk-type-heading">
                  <div className="bk-type-card__label">Headings</div>
                  <div className="bk-type-specimen bk-type-specimen--serif bk-type-specimen--md">A more elegant way to connect.</div>
                  <div className="bk-type-card__meta">Cormorant Garamond · Playfair Display</div>
                  <div className="bk-type-card__hint">Section heads: editorial weight, generous tracking.</div>
                </div>
              </Reveal>
              <Reveal delay={0.24}>
                <div className="bk-type-card" data-testid="bk-type-body">
                  <div className="bk-type-card__label">Body</div>
                  <div className="bk-type-specimen bk-type-specimen--sans">
                    Made for adults who value privacy, beauty, and intention. Modern, clean,
                    quietly confident — body copy should be easy to read at any size.
                  </div>
                  <div className="bk-type-card__meta">Inter · DM Sans · Neue-style sans</div>
                  <div className="bk-type-card__hint">Body: modern, clean, easy to read.</div>
                </div>
              </Reveal>
              <Reveal delay={0.32}>
                <div className="bk-type-card" data-testid="bk-type-accent">
                  <div className="bk-type-card__label">Accent · Labels</div>
                  <div className="bk-type-specimen bk-type-specimen--tracked">PRIVATE · CURATED · INTENTIONAL</div>
                  <div className="bk-type-card__meta">Small uppercase · generous letter-spacing</div>
                  <div className="bk-type-card__hint">Use uppercase sparingly for premium labels and navigation.</div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ─── 4. Voice & Tone ───────────────────────────────────── */}
        <section className="bk-section" data-testid="bk-section-tone">
          <div className="bk-container">
            <Reveal>
              <div className="bk-section__eyebrow">04 · Voice</div>
              <h2 className="bk-h2">Voice &amp; tone</h2>
              <p className="bk-section__lede">
                Elegant. Private. Sensual. Trustworthy. Confident — never loud.
              </p>
            </Reveal>

            <Reveal delay={0.08}>
              <div className="bk-pill-row">
                {['Elegant', 'Private', 'Sensual', 'Trustworthy', 'Feminine', 'Selective', 'Premium', 'Calm', 'Confident'].map((p) => (
                  <span key={p} className="bk-pill">{p}</span>
                ))}
              </div>
            </Reveal>

            <div className="bk-tone-grid">
              <Reveal delay={0.16}>
                <ToneCard
                  title="Do say"
                  accent="do"
                  items={[
                    '“Curated connection”',
                    '“Private by design”',
                    '“Refined chemistry”',
                    '“Discreet introductions”',
                    '“A more elegant way to connect”',
                    '“Made for adults who value privacy, beauty, and intention”',
                  ]}
                />
              </Reveal>
              <Reveal delay={0.24}>
                <ToneCard
                  title="Avoid"
                  accent="avoid"
                  items={[
                    'Cheap pickup language',
                    'Aggressive sales copy',
                    'Explicit wording',
                    'Overpromising outcomes',
                    'Anything that feels unsafe, spammy, or desperate',
                  ]}
                />
              </Reveal>
            </div>
          </div>
        </section>

        {/* ─── 5. Imagery Direction ──────────────────────────────── */}
        <section className="bk-section" data-testid="bk-section-imagery">
          <div className="bk-container">
            <Reveal>
              <div className="bk-section__eyebrow">05 · Imagery</div>
              <h2 className="bk-h2">Imagery direction</h2>
              <p className="bk-section__lede">
                Cinematic, intimate, and editorial — a private members-club mood.
              </p>
            </Reveal>

            <div className="bk-imagery-grid">
              <Reveal delay={0.08}>
                <div className="bk-imagery-card bk-imagery-card--do">
                  <div className="bk-imagery-card__title">Embrace</div>
                  <ul>
                    <li>Cinematic close-ups · soft shadows · editorial portraits</li>
                    <li>Silk, velvet, champagne, marble, candlelight, night-city textures</li>
                    <li>Abstract body-safe forms · silhouettes · hands · pearls · florals · veils · water · glass · smoke</li>
                    <li>Premium nightlife atmosphere · private members-club mood</li>
                  </ul>
                </div>
              </Reveal>
              <Reveal delay={0.16}>
                <div className="bk-imagery-card bk-imagery-card--avoid">
                  <div className="bk-imagery-card__title">Avoid</div>
                  <ul>
                    <li>Harsh neon</li>
                    <li>Stocky corporate photos</li>
                    <li>Explicit imagery</li>
                    <li>Overcrowded scenes</li>
                    <li>Cheap dating-app clichés</li>
                    <li>Cartoon icons</li>
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ─── 6. UI Style ───────────────────────────────────────── */}
        <section className="bk-section" data-testid="bk-section-ui">
          <div className="bk-container">
            <Reveal>
              <div className="bk-section__eyebrow">06 · UI</div>
              <h2 className="bk-h2">UI style</h2>
              <p className="bk-section__lede">
                Slow fades, soft parallax, cinematic reveals — generous negative space.
              </p>
            </Reveal>

            <div className="bk-ui-grid">
              <Reveal delay={0.08}>
                <div className="bk-ui-card">
                  <div className="bk-ui-card__label">Primary button</div>
                  <button type="button" className="bk-btn bk-btn--primary" data-testid="bk-ui-primary-button">
                    Open Canva Brand Kit
                  </button>
                  <div className="bk-ui-card__hint">Bordeaux Silk fill · Rose Gold border · Porcelain text.</div>
                </div>
              </Reveal>
              <Reveal delay={0.16}>
                <div className="bk-ui-card">
                  <div className="bk-ui-card__label">Secondary button</div>
                  <button type="button" className="bk-btn bk-btn--ghost" data-testid="bk-ui-ghost-button">
                    Download Assets
                  </button>
                  <div className="bk-ui-card__hint">Transparent fill · Rose Gold border · Rose Gold text.</div>
                </div>
              </Reveal>
              <Reveal delay={0.24}>
                <div className="bk-ui-card bk-ui-card--principles">
                  <div className="bk-ui-card__label">Principles</div>
                  <ul>
                    <li>Rounded pill buttons · soft glow on hover</li>
                    <li>Dark glassmorphism or champagne soft cards</li>
                    <li>Thin Rose Gold / Smoke Mauve hairlines</li>
                    <li>Minimal line icons · no filled rectangles</li>
                    <li>Editorial spacing · large display type · immersive sections</li>
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ─── 7. Canva Business Integration ─────────────────────── */}
        <section className="bk-section bk-canva" data-testid="bk-section-canva">
          <div className="bk-canva__glow" aria-hidden="true" />
          <div className="bk-container">
            <Reveal>
              <div className="bk-section__eyebrow">07 · Canva</div>
              <h2 className="bk-h2">Canva Business assets</h2>
              <p className="bk-section__lede">
                Access the official NYMPHÉ Canva Business brand kit for social templates,
                presentation decks, launch graphics, ad creatives, and approved visual assets.
              </p>
            </Reveal>

            <Reveal delay={0.08}>
              <div className="bk-canva__cta-row">
                <a
                  className="bk-btn bk-btn--primary"
                  href={CANVA_BRAND_KIT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="bk-canva-primary-cta"
                >
                  Open Canva Brand Kit
                </a>
                <a
                  className="bk-btn bk-btn--ghost"
                  href="mailto:hello@nymphe.co?subject=NYMPHÉ%20Brand%20Kit%20Access"
                  data-testid="bk-canva-request-cta"
                >
                  Request Brand Access
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.16}>
              <div className="bk-canva-grid">
                {[
                  'Social media templates',
                  'Instagram story templates',
                  'Launch graphics',
                  'Press kit',
                  'Logo files',
                  'Colour palette',
                  'Typography guide',
                  'Brand photography references',
                  'Partner materials',
                ].map((t) => (
                  <div key={t} className="bk-canva-card">
                    <div className="bk-canva-card__dot" aria-hidden="true" />
                    {t}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* ─── 8. Asset Library ─────────────────────────────────── */}
        <section id="assets" className="bk-section" data-testid="bk-section-assets">
          <div className="bk-container">
            <Reveal>
              <div className="bk-section__eyebrow">08 · Downloads</div>
              <h2 className="bk-h2">Asset library</h2>
              <p className="bk-section__lede">
                Download approved NYMPHÉ assets — for press, partners, and approved campaigns.
              </p>
            </Reveal>

            <div className="bk-asset-grid">
              <Reveal delay={0.08}>
                <AssetCard
                  title="Primary Logo"
                  desc="NYMPHÉ wordmark — SVG, PNG, and on-light/on-dark variants."
                  href="/assets/nymphe-logo.zip"
                  testid="bk-asset-logo"
                />
              </Reveal>
              <Reveal delay={0.12}>
                <AssetCard
                  title="Monogram"
                  desc="The serif N with crescent accent — favicons, avatars, watermarks."
                  href="/assets/nymphe-logo.zip"
                  testid="bk-asset-monogram"
                />
              </Reveal>
              <Reveal delay={0.16}>
                <AssetCard
                  title="Colour Palette"
                  desc="Full NYMPHÉ palette · hex, RGB, gradient swatches."
                  href="/assets/nymphe-colours.pdf"
                  testid="bk-asset-colours"
                />
              </Reveal>
              <Reveal delay={0.20}>
                <AssetCard
                  title="Typography Guide"
                  desc="Pairings, weights, tracking, and editorial scale."
                  href="/assets/nymphe-media-kit.pdf"
                  testid="bk-asset-typography"
                />
              </Reveal>
              <Reveal delay={0.24}>
                <AssetCard
                  title="Social Templates"
                  desc="Instagram, Story, and launch templates — ready for Canva Business."
                  href="/assets/nymphe-social-templates.pdf"
                  testid="bk-asset-social"
                />
              </Reveal>
              <Reveal delay={0.28}>
                <AssetCard
                  title="Media Kit"
                  desc="Boilerplate, founder bio, brand facts, approved press images."
                  href="/assets/nymphe-media-kit.pdf"
                  testid="bk-asset-media"
                />
              </Reveal>
              <Reveal delay={0.32}>
                <AssetCard
                  title="Press Images"
                  desc="Approved photography — editorial, brand, and launch imagery."
                  href="/assets/nymphe-media-kit.pdf"
                  testid="bk-asset-press"
                />
              </Reveal>
              <Reveal delay={0.36}>
                <AssetCard
                  title="Canva Business Link"
                  desc="Live link to the official Canva brand kit and approved templates."
                  href={CANVA_BRAND_KIT_URL}
                  label="Open Canva"
                  testid="bk-asset-canva"
                />
              </Reveal>
            </div>
          </div>
        </section>

        {/* ─── 9. Legal / Usage Note ────────────────────────────── */}
        <section className="bk-section bk-legal" data-testid="bk-section-legal">
          <div className="bk-container">
            <Reveal>
              <p className="bk-legal__copy">
                NYMPHÉ brand assets are provided for approved use only. Do not alter,
                distort, recolour, or use assets in a way that misrepresents the brand.
                For permissions, contact{' '}
                <a href="mailto:hello@nymphe.co" data-testid="bk-legal-mailto">hello@nymphe.co</a>.
              </p>
            </Reveal>
          </div>
        </section>
      </main>
    </MotionConfig>
  );
};

export default BrandKitPage;
